import React, { useState, useEffect } from 'react';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { UserPlus, Loader2 } from 'lucide-react';
import axios from 'axios';
import { toast } from 'sonner';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

// Country codes
const COUNTRY_CODES = [
  { code: '91', country: 'India', flag: '🇮🇳' },
  { code: '880', country: 'Bangladesh', flag: '🇧🇩' },
  { code: '977', country: 'Nepal', flag: '🇳🇵' }
];

/**
 * Quick Register Customer Component
 * Used in order creation flows to quickly register a new customer
 */
const QuickRegisterCustomer = ({ 
  isOpen, 
  onClose, 
  onCustomerRegistered, 
  token,
  defaultCountryCode = '91'
}) => {
  const [form, setForm] = useState({
    name: '',
    company_name: '',
    mobile: '',
    country_code: defaultCountryCode
  });
  const [registering, setRegistering] = useState(false);

  // Reset form when dialog opens
  useEffect(() => {
    if (isOpen) {
      setForm({
        name: '',
        company_name: '',
        mobile: '',
        country_code: defaultCountryCode
      });
    }
  }, [isOpen, defaultCountryCode]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    // Validation
    if (!form.name.trim()) {
      toast.error('Name is required');
      return;
    }
    if (!form.company_name.trim()) {
      toast.error('Company name is required');
      return;
    }
    if (!form.mobile.trim() || form.mobile.length < 10) {
      toast.error('Valid mobile number is required (min 10 digits)');
      return;
    }

    setRegistering(true);
    try {
      // Determine customer type based on country code
      const isExportCustomer = form.country_code === '880' || form.country_code === '977';
      
      let response;
      if (isExportCustomer) {
        // Register as export customer
        const exportCountry = form.country_code === '880' ? 'bangladesh' : 'nepal';
        response = await axios.post(`${API}/export-orders/customers/register`, {
          name: form.name,
          company_name: form.company_name,
          mobile: form.mobile,
          country_code: form.country_code,
          export_country: exportCountry,
          private_marka: '',
          address: '',
          email: ''
        }, { headers: { Authorization: `Bearer ${token}` } });
        
        if (response.data.success || response.data.customer_id) {
          toast.success(`${exportCountry.charAt(0).toUpperCase() + exportCountry.slice(1)} customer registered!`);
          onCustomerRegistered({
            customer_id: response.data.customer_id,
            name: form.name,
            company_name: form.company_name,
            mobile: form.mobile,
            country_code: form.country_code,
            customer_type: 'export',
            export_country: exportCountry
          });
          onClose();
        }
      } else {
        // Register as domestic customer
        response = await axios.post(`${API}/admin/quick-register-customer`, {
          name: form.name,
          company_name: form.company_name,
          mobile: form.mobile,
          country_code: form.country_code,
          customer_category: 'RETAILER',
          discount_percentage: 0
        }, { headers: { Authorization: `Bearer ${token}` } });
        
        if (response.data.success) {
          toast.success('Customer registered successfully!');
          onCustomerRegistered({
            customer_id: response.data.customer?.customer_id || response.data.customer_id,
            name: form.name,
            company_name: form.company_name,
            mobile: form.mobile,
            country_code: form.country_code,
            discount_percentage: 0,
            customer_category: 'RETAILER'
          });
          onClose();
        }
      }
    } catch (error) {
      const message = error.response?.data?.detail || 'Failed to register customer';
      toast.error(message);
    } finally {
      setRegistering(false);
    }
  };

  const selectedCountry = COUNTRY_CODES.find(c => c.code === form.country_code);

  return (
    <Dialog open={isOpen} onOpenChange={onClose}>
      <DialogContent className="max-w-md max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <UserPlus className="w-5 h-5 text-blue-600" />
            Quick Register Customer
          </DialogTitle>
        </DialogHeader>
        
        <form onSubmit={handleSubmit} className="space-y-4">
          <div>
            <Label htmlFor="name">Name <span className="text-red-500">*</span></Label>
            <Input
              id="name"
              value={form.name}
              onChange={(e) => setForm({ ...form, name: e.target.value })}
              placeholder="Customer name"
              autoFocus
            />
          </div>
          
          <div>
            <Label htmlFor="company_name">Company Name <span className="text-red-500">*</span></Label>
            <Input
              id="company_name"
              value={form.company_name}
              onChange={(e) => setForm({ ...form, company_name: e.target.value })}
              placeholder="Company name"
            />
          </div>
          
          <div>
            <Label htmlFor="mobile">Mobile Number <span className="text-red-500">*</span></Label>
            <div className="flex gap-2">
              <Select 
                value={form.country_code} 
                onValueChange={(value) => setForm({ ...form, country_code: value })}
              >
                <SelectTrigger className="w-32">
                  <SelectValue>
                    {selectedCountry?.flag} +{form.country_code}
                  </SelectValue>
                </SelectTrigger>
                <SelectContent>
                  {COUNTRY_CODES.map(country => (
                    <SelectItem key={country.code} value={country.code}>
                      {country.flag} +{country.code} ({country.country})
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
              <Input
                id="mobile"
                value={form.mobile}
                onChange={(e) => setForm({ ...form, mobile: e.target.value.replace(/\D/g, '').slice(0, 10) })}
                placeholder="Mobile number"
                className="flex-1"
              />
            </div>
            {(form.country_code === '880' || form.country_code === '977') && (
              <p className="text-xs text-green-600 mt-1">
                {form.country_code === '880' ? '🇧🇩 Will be registered as Bangladesh Export Customer' : '🇳🇵 Will be registered as Nepal Export Customer'}
              </p>
            )}
          </div>
          
          <DialogFooter>
            <Button type="button" variant="outline" onClick={onClose}>
              Cancel
            </Button>
            <Button type="submit" disabled={registering}>
              {registering ? (
                <>
                  <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                  Registering...
                </>
              ) : (
                <>
                  <UserPlus className="w-4 h-4 mr-2" />
                  Register & Select
                </>
              )}
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
};

export default QuickRegisterCustomer;
